import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ErrorResponse, FilteredDropDownModelInit, FilteredDropDownRequest, FilteredDropDownResponse, ListModel, MainResponse, RejectedValue } from "shared/interfaces/core-interfaces";
import { AuthHeader } from "services/authHeader";
import { Guid } from "guid-typescript";
import { CreateNoteRequest, initialNoteValue, Note, NoteFilesResponse, NotesSliceModel, UpdateNoteRequest, UploadNoteFilesRequest } from "./notes-interfaces";
import { Activity } from "./activities-interface";
import { API_BASE_URL as baseUri } from "config/env";

export const GetNoteById = createAsyncThunk<Note, Guid, { rejectValue: RejectedValue }>(
    'Slice/NotesSlice/GetNoteById',
    async (NoteId: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/notes/get?noteId=${NoteId}`, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as Note;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetNotesByContact = createAsyncThunk<Note[], Guid, { rejectValue: RejectedValue }>(
    'Slice/NotesSlice/GetNotesByContact',
    async (contactId: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/notes/get-by-contact?contactId=${contactId}`, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as Note[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetNotesByCompany = createAsyncThunk<Note[], Guid, { rejectValue: RejectedValue }>(
    'Slice/NotesSlice/GetNotesByCompany',
    async (companyId: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/notes/get-by-company?companyId=${companyId}`, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as Note[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetNotesByEmployee = createAsyncThunk<Note[], void, { rejectValue: RejectedValue }>(
    'Slice/NotesSlice/GetNotesByEmployee',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/notes/get-by-employee`, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as Note[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateNote = createAsyncThunk<Activity, CreateNoteRequest, { rejectValue: RejectedValue }>(
    'Slice/NotesSlice/CreateNote',
    async (createNoteRequest: CreateNoteRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/notes/create`, {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(createNoteRequest)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as Activity;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateNote = createAsyncThunk<Activity, UpdateNoteRequest, { rejectValue: RejectedValue }>(
    'Slice/ActivitiesSlice/UpdateNote',
    async (updateNoteRequest: UpdateNoteRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/notes/update`, {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(updateNoteRequest)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as Activity;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UploadNoteFile = createAsyncThunk<NoteFilesResponse, UploadNoteFilesRequest, { rejectValue: RejectedValue }>(
    'Slice/ActivitiesSlice/UploadNoteFile',
    async (uploadNoteFilesRequest: UploadNoteFilesRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/notes/upload-files`, {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(uploadNoteFilesRequest)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as NoteFilesResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DeleteNoteFile = createAsyncThunk<NoteFilesResponse, Guid, { rejectValue: RejectedValue }>(
    'Slice/NotesSlice/DeleteNoteFile',
    async (id: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/notes/delete-file?id=${id}`, {
            method: "DELETE",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as NoteFilesResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetPartnersForDropdown = createAsyncThunk<FilteredDropDownResponse, FilteredDropDownRequest, { rejectValue: RejectedValue }>(
    'Slice/NotesSlice/GetPartnersForDropdown',
    async (data: FilteredDropDownRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partners/drop-down', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const getResponse = await response.json() as FilteredDropDownResponse;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetContactsForDropdown = createAsyncThunk<FilteredDropDownResponse, FilteredDropDownRequest, { rejectValue: RejectedValue }>(
    'Slice/ActivityLogsSlice/GetContactsForDropdown',
    async (data: FilteredDropDownRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/contacts/drop-down', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const getResponse = await response.json() as FilteredDropDownResponse;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

const initialValue: NotesSliceModel = {
    note: undefined,
    noteModel: initialNoteValue,
    notesByContact: [],
    notesByCompany: [],
    notesByEmployee: [],

    statusCode: "",
    message: "",
    showNotification: false,
    reloadData: false,

    isLoading: false,

    filteredPartnersList: FilteredDropDownModelInit,
    filteredContactsList: FilteredDropDownModelInit
}

export const NotesSlice = createSlice({
    name: 'NotesSlice',
    initialState: initialValue,
    reducers: {
        resetNotesSlice: (state) => {
            state = initialValue
        },
        resetNoteStatusCode: (state) => {
            state.statusCode = '';
            state.message = '';
            state.showNotification = false;
        },
        resetNoteModelAndLists: (state) => {
            state.noteModel = initialNoteValue;
            state.filteredContactsList = FilteredDropDownModelInit;
            state.filteredPartnersList = FilteredDropDownModelInit;
        },
        setSelectedPartners: (state, action: PayloadAction<ListModel<string>[]>) => {
            if (state.noteModel) {
                state.noteModel.associatedCompanies = action.payload
            }
        },
        setSelectedContacts: (state, action: PayloadAction<ListModel<string>[]>) => {
            if (state.noteModel) {
                state.noteModel.associatedContacts = action.payload
            }
        },
    },
    extraReducers(builder) {
        builder.addCase(GetNoteById.fulfilled, (state, action: PayloadAction<Note>) => {
            state.isLoading = false;
            //state.statusCode = "200";
            state.note = action.payload;
            state.noteModel.id = action.payload.id;
            state.noteModel.associatedCompanies = action.payload.companies;
            state.noteModel.associatedContacts = action.payload.contacts;
            state.noteModel.content = action.payload.content;
            state.noteModel.files = action.payload.files;
        })
            .addCase(GetNoteById.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetNoteById.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(GetNotesByContact.fulfilled, (state, action: PayloadAction<Note[]>) => {
            state.isLoading = false;
            //state.statusCode = "200";
            state.notesByContact = action.payload
        })
            .addCase(GetNotesByContact.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetNotesByContact.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(GetNotesByCompany.fulfilled, (state, action: PayloadAction<Note[]>) => {
            state.isLoading = false;
            //state.statusCode = "200";
            state.notesByCompany = action.payload;
        })
            .addCase(GetNotesByCompany.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetNotesByCompany.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(GetNotesByEmployee.fulfilled, (state, action: PayloadAction<Note[]>) => {
            state.isLoading = false;
            //state.statusCode = "200";
            state.notesByEmployee = action.payload;
        })
            .addCase(GetNotesByEmployee.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetNotesByEmployee.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(CreateNote.fulfilled, (state, action: PayloadAction<Activity>) => {
            state.isLoading = false;
            state.message = "The note is successfully created."
            state.statusCode = "200";
            state.showNotification = true;
        })
            .addCase(CreateNote.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(CreateNote.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(UpdateNote.fulfilled, (state, action: PayloadAction<Activity>) => {
            state.isLoading = false;
            state.message = "The note is successfully updated.";
            state.statusCode = "200";
            state.showNotification = true;
        })
            .addCase(UpdateNote.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(UpdateNote.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(UploadNoteFile.fulfilled, (state, action: PayloadAction<NoteFilesResponse>) => {
            state.isLoading = false;
            state.message = "The note is successfully updated";
            //state.statusCode = "200";
            //state.showNotification = true;
        })
            .addCase(UploadNoteFile.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(UploadNoteFile.pending, state => {
                state.isLoading = true;
            })

        builder.addCase(DeleteNoteFile.fulfilled, (state, action: PayloadAction<NoteFilesResponse>) => {
            state.isLoading = false;
            state.message = "The note is successfully deleted.";
            //state.statusCode = "200";
            //state.showNotification = true;
        })
            .addCase(DeleteNoteFile.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DeleteNoteFile.pending, state => {
                state.isLoading = true;
            })
        //Get Partners list    
        builder.addCase(GetPartnersForDropdown.pending, (state) => {
            state.filteredPartnersList.loading = true;
            state.filteredPartnersList.error = null;
        })
            .addCase(GetPartnersForDropdown.fulfilled, (state, action) => {
                state.filteredPartnersList.loading = false;
                state.filteredPartnersList.data = action.payload.data;
                state.filteredPartnersList.totalItems = action.payload.totalItems;
                state.filteredPartnersList.totalPages = action.payload.totalPages;
                state.filteredPartnersList.currentPage = action.payload.currentPage;
            })
            .addCase(GetPartnersForDropdown.rejected, (state, action) => {
                state.filteredPartnersList.loading = false;
                state.filteredPartnersList.error = action.payload?.message || 'Error fetching data';
            });
        //Get contacts list
        builder.addCase(GetContactsForDropdown.pending, (state) => {
            state.filteredContactsList.loading = true;
            state.filteredContactsList.error = null;
        })
            .addCase(GetContactsForDropdown.fulfilled, (state, action) => {
                state.filteredContactsList.loading = false;
                state.filteredContactsList.data = action.payload.data;
                state.filteredContactsList.totalItems = action.payload.totalItems;
                state.filteredContactsList.totalPages = action.payload.totalPages;
                state.filteredContactsList.currentPage = action.payload.currentPage;
            })
            .addCase(GetContactsForDropdown.rejected, (state, action) => {
                state.filteredContactsList.loading = false;
                state.filteredContactsList.error = action.payload?.message || 'Error fetching data';
            });
    }
});

export const { resetNotesSlice, resetNoteStatusCode, setSelectedPartners, setSelectedContacts, resetNoteModelAndLists } = NotesSlice.actions
export default NotesSlice.reducer;